import { MouseEvent, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import AlertTitle from "@mui/material/AlertTitle";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { approveDailyExpenses } from "../../services/dailyExpense";
import { pushGlobalSuccess } from "../../success/globalSuccess";
import { DailyExpenseReviewParams, DailyExpenseReviewRow } from "../../types/daily-expense.type";
import { todayInBangkok } from "../../utils/date";
import { useDebounce } from "../../utils/hooks/useDebounce";
import { useBranches } from "../SpecialOrder/useBranches";
import ReviewDrawer, { ReviewTarget } from "./ReviewDrawer";
import ReviewFilters, { ReviewFilterValue } from "./ReviewFilters";
import ReviewGapTable from "./ReviewGapTable";
import ReviewTable from "./ReviewTable";
import SkeletonCard from "./SkeletonCard";
import StateCard from "./StateCard";
import { API_DATE_FORMAT, DAILY_EXPENSE_REVIEW_TITLE } from "./constants";
import {
  ALL_STATUSES,
  ReviewLink,
  ReviewView,
  bulkApproveMessage,
  datesBetween,
  emptyListTitleOf,
  isReviewRangeTooLong,
  keepSelectable,
  listTitleOf,
  skippedText,
  toggleAllSelectable,
  toggleUuid,
} from "./dailyExpenseReview";
import { useDailyExpenseReview } from "./useDailyExpenseData";

/**
 * Head office's filter fires one all-branch query per change, so a burst of picks (several
 * branches, a range drawn day by day) settles before it is sent.
 */
const FILTER_DEBOUNCE_MS = 400;

/** A document the bulk approve skipped, keyed by its uuid. */
interface SkippedLine {
  uuid: string;
  text: string;
}

/** From the 1st of the month to today, every branch, รออนุมัติ — unless a link presets them. */
const initialFilterOf = (link: ReviewLink): ReviewFilterValue => {
  const today = todayInBangkok();
  return {
    dateFrom: link.start ? dayjs(link.start) : today.startOf("month"),
    dateTo: link.end ? dayjs(link.end) : today,
    branchCodes: link.branchCode ? [link.branchCode] : [],
    status: link.status ?? "SUBMITTED",
  };
};

interface DailyExpenseReviewScreenProps {
  /** A preset from the query string (S4's warning strip links branch by branch). */
  initialLink?: ReviewLink;
}

/**
 * S3 อนุมัติค่าใช้จ่ายรายวัน: the documents branches handed in, filtered by range, branches and
 * status (รออนุมัติ by default), with a drawer to approve or send back one and อนุมัติที่เลือก for
 * many; and the branch × day view of who has not handed a day in yet. Both views come from the
 * same request.
 */
export default function DailyExpenseReviewScreen({
  initialLink = {},
}: Readonly<DailyExpenseReviewScreenProps>) {
  const branches = useBranches(true);
  const [view, setView] = useState<ReviewView>(initialLink.view ?? "LIST");
  const [filter, setFilter] = useState<ReviewFilterValue>(() => initialFilterOf(initialLink));
  const settledFilter = useDebounce(filter, FILTER_DEBOUNCE_MS);
  const isRangeTooLong = isReviewRangeTooLong(filter.dateFrom, filter.dateTo);
  const params = useMemo<DailyExpenseReviewParams | null>(
    () =>
      isReviewRangeTooLong(settledFilter.dateFrom, settledFilter.dateTo)
        ? null
        : {
            start: settledFilter.dateFrom.format(API_DATE_FORMAT),
            end: settledFilter.dateTo.format(API_DATE_FORMAT),
            branchCodes: settledFilter.branchCodes,
            status: settledFilter.status === ALL_STATUSES ? null : settledFilter.status,
          },
    [settledFilter],
  );
  const review = useDailyExpenseReview(params);
  const rows = useMemo(() => review.data?.rows ?? [], [review.data]);
  const [selected, setSelected] = useState<string[]>([]);
  const [isApproving, setApproving] = useState(false);
  const [skippedLines, setSkippedLines] = useState<SkippedLine[]>([]);
  const [target, setTarget] = useState<ReviewTarget | null>(null);

  // A new filter is a new list: ticks and the previous bulk outcome belong to the old one.
  useEffect(() => {
    setSelected([]);
    setSkippedLines([]);
  }, [params]);

  const visibleSelected = keepSelectable(selected, rows);
  const dates = useMemo(
    () => (params ? datesBetween(settledFilter.dateFrom, settledFilter.dateTo) : []),
    [params, settledFilter],
  );

  const handleViewChange = (_event: MouseEvent<HTMLElement>, next: ReviewView | null) => {
    if (next) setView(next);
  };
  const handleFilterChange = (patch: Partial<ReviewFilterValue>) =>
    setFilter((previous) => ({ ...previous, ...patch }));
  const toggleRow = (uuid: string) => setSelected((previous) => toggleUuid(previous, uuid));
  const toggleAll = () => setSelected((previous) => toggleAllSelectable(previous, rows));
  const openRow = (row: DailyExpenseReviewRow) =>
    setTarget({ branchCode: row.branch_code, businessDate: row.business_date });
  const openCell = (branchCode: string, businessDate: string) =>
    setTarget({ branchCode, businessDate });
  const closeDrawer = () => setTarget(null);
  const handleDocumentChanged = () => {
    setTarget(null);
    review.reload();
  };
  const dismissSkipped = () => setSkippedLines([]);

  const approveSelected = async () => {
    setApproving(true);
    try {
      const { data } = await approveDailyExpenses(visibleSelected);
      const skipped = data.data?.skipped ?? [];
      pushGlobalSuccess(bulkApproveMessage(data.data?.approved.length ?? 0, skipped.length));
      setSkippedLines(skipped.map((item) => ({ uuid: item.uuid, text: skippedText(item, rows) })));
      setSelected([]);
      review.reload();
    } catch {
      // The interceptor has already shown the server's message; the ticks stay for a retry.
    } finally {
      setApproving(false);
    }
  };

  const isListEmpty = review.data !== null && rows.length === 0;
  const isGapEmpty = review.data !== null && review.data.gap.length === 0;

  return (
    <Stack sx={{ py: 1 }} gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="h6" fontWeight={600}>
          {DAILY_EXPENSE_REVIEW_TITLE}
        </Typography>
        <ToggleButtonGroup exclusive size="small" value={view} onChange={handleViewChange}>
          <ToggleButton value="LIST">รายการเอกสาร</ToggleButton>
          <ToggleButton value="GAP">สาขาที่ยังไม่ส่ง</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      <ReviewFilters
        value={filter}
        branches={branches}
        isStatusDisabled={view === "GAP"}
        isRangeTooLong={isRangeTooLong}
        onChange={handleFilterChange}
      />

      {skippedLines.length > 0 && (
        <Alert severity="warning" onClose={dismissSkipped}>
          <AlertTitle>ข้าม {skippedLines.length} ใบ เพราะสถานะเปลี่ยนไประหว่างนั้น</AlertTitle>
          {skippedLines.map((line) => (
            <div key={line.uuid}>{line.text}</div>
          ))}
        </Alert>
      )}

      {review.isLoading && <SkeletonCard />}
      {review.isFailed && (
        <StateCard
          title="โหลดรายการไม่สำเร็จ"
          text="เชื่อมต่อเซิร์ฟเวอร์ไม่ได้ ค่าใน filter ยังอยู่"
          onRetry={review.reload}
        />
      )}

      {review.data && view === "LIST" && isListEmpty && (
        <StateCard
          title={emptyListTitleOf(settledFilter.status)}
          text="ลองเปลี่ยนช่วงวันที่ สาขา หรือสถานะ"
        />
      )}
      {review.data && view === "LIST" && !isListEmpty && (
        <ReviewTable
          title={listTitleOf(settledFilter.status)}
          rows={rows}
          totals={review.data.totals}
          selected={visibleSelected}
          isApproving={isApproving}
          onToggle={toggleRow}
          onToggleAll={toggleAll}
          onOpen={openRow}
          onApproveSelected={approveSelected}
        />
      )}

      {review.data && view === "GAP" && isGapEmpty && (
        <StateCard title="ไม่มีสาขาที่วิ่งในช่วงที่เลือก" text="ลองเปลี่ยนช่วงวันที่หรือสาขา" />
      )}
      {review.data && view === "GAP" && !isGapEmpty && (
        <ReviewGapTable gap={review.data.gap} dates={dates} onOpen={openCell} />
      )}

      <ReviewDrawer
        target={target}
        onClose={closeDrawer}
        onChanged={handleDocumentChanged}
        onStale={review.reload}
      />
    </Stack>
  );
}
